import { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { resolveInviteCustomerId } from './dataRequestSend';
import SigningEmailReview from './SigningEmailReview';
import { useAuth } from '../context/AuthContext';
import {
  prepareDataRequest, previewDataRequestEmail, sendPreparedDataRequest, discardPreparedDataRequest,
} from '../lib/dataRequestApi';

interface Props {
  customerId?: number;
  prefillEmail?: string;
  onClose: () => void;
  onSuccess?: () => void;
  onResolveCustomerId?: (email: string) => Promise<number | undefined>;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "Daten anfordern" — bei bereits bekanntem Kunden (E-Mail hinterlegt, kein Resolver mit
 *  Seiteneffekten wie Kunden-Neuanlage) direkt die "E-Mail prüfen"-Komponente (Schritt
 *  "review"); sonst erst E-Mail-Adresse abfragen (Schritt "form"). Die Checkbox
 *  "Dauerdokumente mitschicken" sitzt in beiden Fällen im Kopf von SigningEmailReview. */
export function DataRequestModal({ customerId, prefillEmail, onClose, onSuccess, onResolveCustomerId }: Props) {
  const { user } = useAuth();
  const canSkipForm = !onResolveCustomerId && !!customerId && !!prefillEmail?.trim() && EMAIL_RE.test(prefillEmail.trim());
  const [step, setStep] = useState<'form' | 'review'>(canSkipForm ? 'review' : 'form');
  const [email, setEmail] = useState(prefillEmail || '');
  const [includeDauerdokumente, setIncludeDauerdokumente] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  // Erst nach resolveInviteCustomerId bekannt (kann einen neuen Kunden anlegen) —
  // SigningEmailReview braucht die ID für Vorschau/Vorbereiten.
  const [resolvedCustomerId, setResolvedCustomerId] = useState<number | undefined>(customerId);

  const handleNext = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    try {
      const cid = await resolveInviteCustomerId(email.trim(), customerId, onResolveCustomerId);
      setResolvedCustomerId(cid);
      setStep('review');
    } catch (err: any) {
      setError(err.message || 'Fehler beim Vorbereiten.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setStep('form'); onClose(); };

  if (done) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
          <div className="text-center py-4">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
            <p className="text-sm text-slate-700 font-medium">E-Mail gesendet!</p>
            <p className="text-xs text-slate-500 mt-1">Der Kunde erhält in Kürze einen Link zum Ausfüllen.</p>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200">
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    return (
      <SigningEmailReview
        open
        email={email}
        context={{}}
        prepare={() => prepareDataRequest(email, resolvedCustomerId, includeDauerdokumente)
          .then(p => ({ token: p.token, signingUrl: p.formUrl, ready: true }))}
        fetchPreviewFn={body => previewDataRequestEmail(resolvedCustomerId, includeDauerdokumente, body)
          .then(r => ({ defaultSubject: r.defaultSubject, defaultMessage: r.defaultMessage, defaultAnrede: r.defaultAnrede, defaultClosing: '', defaultKontoauszug: '', subject: r.subject, html: r.html }))}
        sendFn={(token, body) => sendPreparedDataRequest(token, { email: body.customerEmail, subject: body.subject, anrede: body.anrede, message: body.message, cc: body.cc })}
        discardFn={discardPreparedDataRequest}
        defaultCc={user?.email}
        extraToggle={{
          label: 'Dauerdokumente mitschicken (Vollmacht, GRC- und De-Minimis-Erklärung)',
          checked: includeDauerdokumente,
          onChange: setIncludeDauerdokumente,
        }}
        onCancel={canSkipForm ? handleClose : () => setStep('form')}
        onSent={() => { setDone(true); onSuccess?.(); }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900">Daten anfordern</h3>
          <button onClick={handleClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Der Kunde erhält eine E-Mail mit einem sicheren Einmal-Link zum Ausfüllen seiner Daten.
        </p>
        <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail-Adresse</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleNext()}
          placeholder="kunde@beispiel.de"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 mb-3"
          autoFocus
        />
        <label className="flex items-start gap-2 mb-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeDauerdokumente}
            onChange={e => setIncludeDauerdokumente(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
          />
          <span className="text-xs text-slate-600">
            Dauerdokumente mitschicken (Vollmacht, GRC- und De-Minimis-Erklärung)
          </span>
        </label>
        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
        <div className="flex gap-2 justify-end">
          <button onClick={handleClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            Abbrechen
          </button>
          <button
            onClick={handleNext}
            disabled={!email.trim() || loading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Vorschau
          </button>
        </div>
      </div>
    </div>
  );
}
